import { Static, Type } from '@sinclair/typebox';
import { FileUploadSchema } from '../../shared/file/file-request.schema';
import { IsoDateTimeSchema } from '../../shared/utils/date-time.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { StringEnum } from '../../utils/typebox';
import { SendMessageAttachmentsSchema } from '../message/message-attachment-request.schema';

const ListChatsFiltersSchema = Type.Object({
  type: Type.Optional(
    StringEnum(['1to1', 'group', 'channel'], {
      description: `Return only chats of the given type (if supported by the provider).
        - \`1to1\` is a 1to1 chat.
        - \`group\` is a group chat.
        - \`channel\` is a channel chat.`,
    }),
  ),
  before: Type.Optional(
    IsoDateTimeSchema({
      description: 'A filter to target items created before the datetime (exclusive).',
    }),
  ),
  after: Type.Optional(
    IsoDateTimeSchema({
      description: 'A filter to target items created after the datetime (exclusive).',
    }),
  ),
  is_archived: Type.Optional(
    Type.Boolean({
      description: 'Return only chats of the given archived status (if supported by the provider).',
    }),
  ),
  is_unread: Type.Optional(
    Type.Boolean({
      description: 'Return only chats of the given unread status (if supported by the provider).',
    }),
  ),
});

export const GetChatsListQuerySchema = Type.Intersect([PaginationQueryParamsSchema, ListChatsFiltersSchema]);

export const GetInboxChatsListParamsSchema = Type.Object({
  inbox_id: Type.String({
    minLength: 1,
    description:
      'ID of the Inbox to retrieve Chats from. Use <a href="https://developer.unipile.com/v2.0/reference/getinboxeslist">List all Inboxes</a> to get the ID of an inbox.',
  }),
});

export const GetChatParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'ID of the Chat to retrieve.' }),
});

export const GetChatPictureParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'ID of the Chat whose picture must be downloaded.' }),
});

export const GetUserChatParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'ID of the User whose existing 1to1 Chat should be retrieved.' }),
});

export const StartChatFromInboxParamsSchema = Type.Object({
  inbox_id: Type.String({
    minLength: 1,
    description:
      'ID of the Inbox to start the Chat in. Use <a href="https://developer.unipile.com/v2.0/reference/getinboxeslist">List all Inboxes</a> to get the ID of an inbox.',
  }),
});

export const StartChatBaseBodySchema = Type.Object(
  {
    text: Type.String({
      description:
        "The textual content of the first message to be sent in the chat. It's recommended not to use HTML or Markdown. Use empty string to start a chat without a message (if supported by the provider).",
    }),
    name: Type.Optional(
      Type.String({
        description:
          'A custom name for the chat. Some providers allow custom names only on groups. Default is made of participant names.',
      }),
    ),
    users_ids: Type.Union(
      [
        Type.Array(Type.String({ minLength: 1 }), {
          minItems: 1,
          description:
            'An array of user IDs to start a `group` chat. Passing an array with a single user id will attempt to start a group with that user only.',
        }),
        Type.String({
          minLength: 1,
          description: 'A single user ID to start a `1to1` chat.',
          /** @todo Integration guide :  For `LinkedIn` company messaging, this has to be the company mailbox ID. */
        }),
      ],
      {
        description:
          'Participant(s) of the chat. To start a 1to1 chat, pass a single user id as a string. To start a group, pass an array of user ids. Passing an array with a single user id will attempt to start a group with that user only.',
      },
    ),
    attachments: SendMessageAttachmentsSchema,
  },
  { additionalProperties: false },
);

export const UpdateChatParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'ID of the Chat to update.' }),
});

export const DeleteChatParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'ID of the Chat to delete.' }),
});

export const AcceptChatRequestParamsSchema = Type.Object({
  inbox_id: Type.String({
    description:
      'ID of the Inbox where the chat request currently lives. Use <a href="https://developer.unipile.com/v2.0/reference/get_v2-account-id-inboxes">List all Inboxes</a> to get the ID of an inbox.',
  }),
  chat_id: Type.String({ description: 'ID of the Chat request to accept.' }),
});

export const UpdateChatBodySchema = Type.Object(
  {
    name: Type.Optional(
      Type.String({ description: 'A custom name for the chat. Some providers allow custom names only on groups.' }),
    ),
    picture: Type.Optional(FileUploadSchema),
    pin_status: Type.Optional(
      Type.Boolean({
        description: 'If supported, set to `true` to pin the chat at the top of the list, or `false` to unpin it.',
      }),
    ),
    archive_status: Type.Optional(
      Type.Boolean({
        description: 'If supported, set to `true` to archive the chat, or `false` to move it back to the main inbox.',
      }),
    ),
    label: Type.Optional(
      Type.String({
        description:
          'If supported, associate the chat with the given label. For Whatsapp, this is resolved by label name and created if missing.',
      }),
    ),
    muted_until: Type.Optional(
      Type.Union([Type.Boolean(), IsoDateTimeSchema()], {
        description:
          'If supported, the date until which the conversation should be muted. Else `true` to mute the chat for an indefinite time, else `false` to un-mute an already muted chat.',
      }),
    ),
    read_status: Type.Optional(
      Type.Boolean({
        description:
          'Set to `true` to mark the chat as read. This is useful when displaying the list of messages in your app. Set to `false` to mark the chat as unread.',
      }),
    ),
  },
  { additionalProperties: false },
);

// GetInboxChatsListParamsSchema
export const PostComposingParamsSchema = Type.Object({
  chat_id: Type.String({ minLength: 1, description: 'ID of the Chat where the composing action is taking place.' }),
});

export const PostComposingQuerySchema = Type.Object({
  action: StringEnum(['typing', 'recording'], {
    description: `The composing status of a user in a chat.
    - \`typing\` if the user is typing / sending a message. This presence must target a chat.
    - \`recording\` if the user is recording a voice note. This presence must target a chat.`,
  }),
});

export type GetChatsListQuery = Static<typeof GetChatsListQuerySchema>;
export type GetInboxChatsListParams = Static<typeof GetInboxChatsListParamsSchema>;
export type GetChatParams = Static<typeof GetChatParamsSchema>;
export type GetChatPictureParams = Static<typeof GetChatPictureParamsSchema>;
export type GetUserChatParams = Static<typeof GetUserChatParamsSchema>;
export type StartChatFromInboxParams = Static<typeof StartChatFromInboxParamsSchema>;
export type StartChatBody<Specifics = object> = Omit<Static<typeof StartChatBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type UpdateChatParams = Static<typeof UpdateChatParamsSchema>;
export type DeleteChatParams = Static<typeof DeleteChatParamsSchema>;
export type AcceptChatRequestParams = Static<typeof AcceptChatRequestParamsSchema>;
export type UpdateChatBody = Static<typeof UpdateChatBodySchema>;
export type ComposingParams = Static<typeof PostComposingParamsSchema>;
export type ComposingQuery = Static<typeof PostComposingQuerySchema>;
